import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import toast from "react-hot-toast";
import Select from "react-select";
import HomeService from "../Services/HomeService";

const headLineStyle = {
  textAlign: "center",
  fontSize: "30px",
  color: "#868e96",
  margin: "15px",
  fontWeight: "bold",
};

const ManagePrimeLocationInsights = () => {
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editID, setEditID] = useState("");
  const [filterText, setFilterText] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    city: "",
    localities: [],
    apartments: [],
    commercials: [""],
    url: "",
    propertyIds: [], // Added propertyIds field
  });

  // Dropdown data
  const [allCities, setAllCities] = useState([]);
  const [allLocalities, setAllLocalities] = useState([]);
  const [allApartments, setAllApartments] = useState([]);
  const [filteredLocalities, setFilteredLocalities] = useState([]);
  const [filteredApartments, setFilteredApartments] = useState([]);
  const [properties, setProperties] = useState([]); // Added properties state
  const [selectedProperties, setSelectedProperties] = useState([]); // Added selected properties state

  // Filter data
  const filter = () => {
    if (!filterText) {
      setFilteredData(insights);
      return;
    }

    const arr = insights?.filter((e) => {
      const cityName = e?.city?.name?.toLowerCase() || "";
      const searchText = filterText.toLowerCase();
      return cityName.includes(searchText);
    });
    setFilteredData(arr);
  };

  useEffect(() => {
    filter();
  }, [filterText, insights]);

  // Fetch all data
  useEffect(() => {
    fetchInsights();
    fetchCities();
    fetchLocalities();
    fetchApartments();
  }, []);

  // Fetch insights
  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await HomeService.getGetPrimeLocationInsights();
      if (res?.data) {
        const transformedData = res.data.map((insight, index) => ({
          ...insight,
          sln: index + 1,
        }));
        setInsights(transformedData);
        setFilteredData(transformedData);
      }
    } catch (error) {
      console.error("Error fetching insights:", error);
      toast.error("Failed to load insights!");
    } finally {
      setLoading(false);
    }
  };

  // Fetch cities
  const fetchCities = async () => {
    try {
      const res = await HomeService.getCities();
      if (res?.data) {
        setAllCities(res.data);
      }
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  };

  // Fetch localities
  const fetchLocalities = async () => {
    try {
      const res = await HomeService.getLocalities();
      if (res?.data) {
        setAllLocalities(res.data);
      }
    } catch (error) {
      console.error("Error fetching localities:", error);
    }
  };

  // Fetch apartments
  const fetchApartments = async () => {
    try {
      const res = await HomeService.getApartment();
      if (res?.data) {
        setAllApartments(res.data);
      }
    } catch (error) {
      console.error("Error fetching apartments:", error);
    }
  };

  // Fetch properties with city ID
  const fetchPropertiesWithCityId = async (cityId) => {
    if (!cityId) {
      setProperties([]);
      setSelectedProperties([]);
      return;
    }

    try {
      setLoadingProperties(true);
      const res = await HomeService?.filterProperty({ cityId }, 1, 5000);
      if (res?.status) {
        const filterCity = res?.data?.filter((item) => {
          return item?.aptName !== "";
        });
        const modifiedProperty = filterCity?.map((item) => {
          return {
            label: `Apartment: ${item?.aptName}, Address: ${item?.address}`,
            value: item?._id,
          };
        });
        setProperties(modifiedProperty);
      } else {
        toast.error(res?.message);
      }
    } catch (error) {
      console.log(error);
      toast.error(error?.message || "Failed to fetch properties");
    } finally {
      setLoadingProperties(false);
    }
  };

  // Update filtered localities when city changes
  useEffect(() => {
    if (formData.city) {
      const filtered = allLocalities.filter(
        (locality) => locality?.city?._id === formData.city,
      );
      const formattedLocalities = filtered.map((locality) => ({
        value: locality._id,
        label: `${locality.name} (${locality.city?.name})`,
      }));
      setFilteredLocalities(formattedLocalities);
    } else {
      setFilteredLocalities([]);
    }
  }, [formData.city, allLocalities]);

  // Update filtered apartments when city changes
  useEffect(() => {
    if (formData.city) {
      const filtered = allApartments.filter(
        (apartment) => apartment?.city?._id === formData.city,
      );
      const formattedApartments = filtered.map((apartment) => ({
        value: apartment._id,
        label: `${apartment.aptName} - ${apartment.locality?.name || "N/A"}`,
      }));
      setFilteredApartments(formattedApartments);
    } else {
      setFilteredApartments([]);
    }
  }, [formData.city, allApartments]);

  // Handle edit
  const handleEdit = (row) => {
    setIsEdit(true);
    setEditID(row._id);

    // Set form data from row
    const selectedLocalities =
      row.localities?.map((locality) => ({
        value: locality._id,
        label: locality.name,
      })) || [];

    const selectedApartments =
      row.apartments?.map((apartment) => ({
        value: apartment._id,
        label: apartment.aptName,
      })) || [];

    // Format properties for selected properties
    const selectedProps =
      row.propertyIds?.map((property) => ({
        value: property._id,
        label: `Apartment: ${property.aptName}, Address: ${property.address}`,
      })) || [];

    setSelectedProperties(selectedProps);

    // Fetch properties for the city
    if (row.city?._id) {
      fetchPropertiesWithCityId(row.city._id);
    }

    setFormData({
      city: row.city?._id || row.city || "",
      localities: selectedLocalities,
      apartments: selectedApartments,
      commercials: row.commercials?.length > 0 ? row.commercials : [""],
      url: row.url || "",
      propertyIds: row.propertyIds?.map((p) => p._id) || [],
    });
  };

  // Handle delete
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete Insight?",
      text: "Are you sure you want to delete this prime location insight?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        const res = await HomeService.deletePrimeLocationInsights(id);
        if (res?.status) {
          Swal.fire("Deleted!", "Insight has been deleted.", "success");
          fetchInsights();
        } else {
          toast.error(res?.message || "Failed to delete insight");
        }
      } catch (error) {
        Swal.fire("Error!", "Failed to delete insight.", "error");
      }
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Fetch properties when city changes
    if (name === "city") {
      fetchPropertiesWithCityId(value);
      setSelectedProperties([]);
    }
  };

  // Handle locality selection
  const handleLocalityChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      localities: selectedOptions || [],
    }));
  };

  // Handle apartment selection
  const handleApartmentChange = (selectedOptions) => {
    setFormData((prev) => ({
      ...prev,
      apartments: selectedOptions || [],
    }));
  };

  // Handle property selection
  const handlePropertyChange = (selectedOptions) => {
    setSelectedProperties(selectedOptions || []);
    setFormData((prev) => ({
      ...prev,
      propertyIds: (selectedOptions || []).map((option) => option.value),
    }));
  };

  // Handle commercial input change
  const handleCommercialChange = (index, value) => {
    const updatedCommercials = [...formData.commercials];
    updatedCommercials[index] = value;
    setFormData((prev) => ({
      ...prev,
      commercials: updatedCommercials,
    }));
  };

  // Add new commercial field
  const addCommercialField = () => {
    if (formData.commercials.length < 10) {
      setFormData((prev) => ({
        ...prev,
        commercials: [...prev.commercials, ""],
      }));
    } else {
      toast.info("Maximum 10 commercial spaces allowed");
    }
  };

  // Remove commercial field
  const removeCommercialField = (index) => {
    if (formData.commercials.length > 1) {
      const updatedCommercials = formData.commercials.filter(
        (_, i) => i !== index,
      );
      setFormData((prev) => ({
        ...prev,
        commercials: updatedCommercials,
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.city) {
      toast.error("Please select a city");
      return;
    }

    if (formData.localities.length === 0) {
      toast.error("Please select at least one locality");
      return;
    }

    if (formData.apartments.length === 0) {
      toast.error("Please select at least one apartment");
      return;
    }

    // if (formData.propertyIds.length === 0) {
    //   toast.error("Please select at least one property");
    //   return;
    // }

    const filteredCommercials = formData.commercials.filter(
      (com) => com.trim() !== "",
    );
    if (filteredCommercials.length === 0) {
      toast.error("Please add at least one commercial space");
      return;
    }

    if (!formData.url) {
      toast.error("Please enter a URL");
      return;
    }

    try {
      new URL(formData.url);
    } catch {
      toast.error("Please enter a valid URL");
      return;
    }

    // Prepare data
    const data = {
      city: formData.city,
      localities: formData.localities.map((loc) => loc.value),
      apartments: formData.apartments.map((apt) => apt.value),
      commercials: filteredCommercials,
      url: formData.url,
      propertyIds: formData.propertyIds,
    };

    try {
      let res;
      if (isEdit) {
        res = await HomeService.updatePrimeLocationInsights(editID, data);
      } else {
        res = await HomeService.createPrimeLocationInsights(data);
      }

      if (res?.status) {
        toast.success(
          res?.message ||
            (isEdit ? "Updated successfully!" : "Created successfully!"),
        );
        resetForm();
        fetchInsights();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save");
      console.error("Error saving insight:", error);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      city: "",
      localities: [],
      apartments: [],
      commercials: [""],
      url: "",
      propertyIds: [],
    });
    setSelectedProperties([]);
    setProperties([]);
    setIsEdit(false);
    setEditID("");
  };

  // DataTable columns
  const columns = [
    {
      name: "S.No.",
      selector: (row) => row.sln,
      sortable: true,
      width: "8%",
    },
    {
      name: "City",
      cell: (row) => row.city?.name || row.city || "N/A",
      sortable: true,
      width: "15%",
    },
    {
      name: "Localities",
      cell: (row) => row.localities?.length || 0,
      sortable: true,
      width: "12%",
      center: true,
    },
    {
      name: "Apartments",
      cell: (row) => row.apartments?.length || 0,
      sortable: true,
      width: "12%",
      center: true,
    },
    {
      name: "Commercials",
      cell: (row) => row.commercials?.length || 0,
      sortable: true,
      width: "12%",
      center: true,
    },
    {
      name: "Properties",
      cell: (row) => row.propertyIds?.length || 0,
      sortable: true,
      width: "12%",
      center: true,
    },
    {
      name: "URL",
      cell: (row) => (
        <a
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{ fontSize: "12px" }}
          title={row.url}
        >
          {row.url?.length > 20
            ? `${row.url.substring(0, 20)}...`
            : row.url || "N/A"}
        </a>
      ),
      width: "15%",
    },
    {
      name: "Action",
      cell: (row) => (
        <>
          <span title="Edit" onClick={() => handleEdit(row)}>
            <i
              className="fa-solid fa-pen-to-square"
              style={{
                cursor: "pointer",
                padding: "5px",
                marginRight: "5px",
                fontSize: "1.5em",
                color: "rgb(133, 218, 79)",
                backgroundColor: "#E9F3F7",
              }}
            ></i>
          </span>
          <span title="Delete" onClick={() => handleDelete(row._id)}>
            <i
              className="fa-solid fa-trash-can text-danger"
              style={{
                cursor: "pointer",
                padding: "5px",
                marginRight: "5px",
                fontSize: "1.5em",
                color: "#477DA5",
                backgroundColor: "#E9F3F7",
              }}
            ></i>
          </span>
        </>
      ),
      width: "10%",
    },
  ];

  return (
    <div className="main_wrap" style={{ marginTop: "6rem" }}>
      <div className="container-fluid">
        <div className="mt-5">
          <div style={headLineStyle} className="page-headline">
            Manage Prime Location Insights
          </div>

          {loading ? (
            <p>Loading...</p>
          ) : (
            <>
              {isEdit ? (
                <section className="chartBox_area">
                  <h2>{isEdit ? "Edit" : "Add"} Prime Location Insight</h2>
                  <hr />
                  <form onSubmit={handleSubmit}>
                    <div className="row">
                      {/* City */}
                      <div className="col-md-6 mt-3">
                        <label>
                          City<span className="text-danger">*</span>
                        </label>
                        <select
                          className="form-control"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                        >
                          <option value="">---Select City---</option>
                          {allCities.map((city) => (
                            <option key={city._id} value={city._id}>
                              {city.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* URL */}
                      <div className="col-md-6 mt-3">
                        <label>
                          Location URL<span className="text-danger">*</span>
                        </label>
                        <input
                          type="url"
                          className="form-control"
                          name="url"
                          value={formData.url}
                          onChange={handleInputChange}
                          placeholder="https://example.com/prime-locations"
                          required
                        />
                      </div>

                      {/* Localities (only show when city is selected) */}
                      {formData.city && (
                        <div className="col-md-6 mt-3">
                          <label>
                            Localities<span className="text-danger">*</span>
                          </label>
                          <Select
                            isMulti
                            options={filteredLocalities}
                            value={formData.localities}
                            onChange={handleLocalityChange}
                            placeholder="Select localities..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                          <small className="text-muted">
                            Selected: {formData.localities.length} localities
                          </small>
                        </div>
                      )}

                      {/* Apartments (only show when city is selected) */}
                      {formData.city && (
                        <div className="col-md-6 mt-3">
                          <label>
                            Apartments<span className="text-danger">*</span>
                          </label>
                          <Select
                            isMulti
                            options={filteredApartments}
                            value={formData.apartments}
                            onChange={handleApartmentChange}
                            placeholder="Select apartments..."
                            className="react-select-container"
                            classNamePrefix="react-select"
                          />
                          <small className="text-muted">
                            Selected: {formData.apartments.length} apartments
                          </small>
                        </div>
                      )}

                      {/* Properties (only show when city is selected) */}
                      {/* {formData.city && (
                        <div className="col-md-12 mt-3">
                          <label>
                            Properties<span className="text-danger">*</span>
                          </label>
                          <Select
                            isMulti
                            options={properties}
                            value={selectedProperties}
                            onChange={handlePropertyChange}
                            placeholder={
                              loadingProperties
                                ? "Loading Properties..."
                                : "Search and select Properties..."
                            }
                            className="react-select-container"
                            classNamePrefix="react-select"
                            isClearable={true}
                            isSearchable={true}
                            isLoading={loadingProperties}
                            noOptionsMessage={() =>
                              loadingProperties
                                ? "Loading..."
                                : "No Properties found"
                            }
                          />
                          <small className="text-muted">
                            Selected: {selectedProperties.length} Properties
                          </small>
                        </div>
                      )} */}

                      {/* Commercial Spaces */}
                      <div className="col-md-12 mt-3">
                        <label>
                          Commercial Spaces
                          <span className="text-danger">*</span>
                        </label>
                        <div className="card">
                          <div className="card-body">
                            {formData.commercials.map((commercial, index) => (
                              <div key={index} className="input-group mb-2">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={commercial}
                                  onChange={(e) =>
                                    handleCommercialChange(
                                      index,
                                      e.target.value,
                                    )
                                  }
                                  placeholder={`Commercial Space ${index + 1}`}
                                />
                                <div className="input-group-append">
                                  {formData.commercials.length > 1 && (
                                    <button
                                      type="button"
                                      className="btn btn-danger"
                                      onClick={() =>
                                        removeCommercialField(index)
                                      }
                                      title="Remove"
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary mt-2"
                              onClick={addCommercialField}
                            >
                              <i className="fas fa-plus mr-2"></i>
                              Add More Commercial Space
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-md-12 mt-4">
                        <button type="submit" className="btn btn-primary">
                          {isEdit ? "Update Insight" : "Add Insight"}
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary ml-2"
                          onClick={resetForm}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </form>
                </section>
              ) : (
                <section className="chartBox_area">
                  {/* Search Input */}
                  <div className="mb-3" style={{ textAlign: "right" }}>
                    <input
                      type="text"
                      placeholder="Search by city name..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                      style={{
                        padding: "8px 12px",
                        border: "1px solid #ddd",
                        borderRadius: "5px",
                        width: "300px",
                      }}
                    />
                  </div>
                  <DataTable
                    columns={columns}
                    data={filterText ? filteredData : insights}
                    pagination
                    responsive
                    highlightOnHover
                    noDataComponent="No prime location insights found"
                  />
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManagePrimeLocationInsights;
